import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import AnimatedModal from "components/layout/motion/AnimatedModal";
import FunctionalButton from "components/layout/FunctionalButton";
import { ReactComponent as CloseIcon } from "assets/svgs/Close.svg";
import { useARC } from "providers/ARC/ARCProvider";
import { useToast } from "providers/ToastProvider";
import {
    getThermalThreshold,
    putThermalThreshold,
} from "lib/thermalDetectionApi";
import {
    getGimbalGatewayBaseUrl,
    getGimbalThermalPalette,
    getGimbalThermalPaletteModes,
    postGimbalThermalPalette,
    type ThermalPaletteInfo,
} from "lib/gimbalGatewayApi";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import useFleetArcId from "lib/utils/useFleetArcId";

const RANGE_CLASS =
    "h-4 w-full min-w-[6rem] flex-1 cursor-pointer bg-transparent " +
    "appearance-none accent-primary2 outline-none " +
    "[&::-webkit-slider-runnable-track]:h-1.5 [&::-webkit-slider-runnable-track]:rounded-full " +
    "[&::-webkit-slider-runnable-track]:bg-black70 " +
    "[&::-webkit-slider-thumb]:-mt-0.5 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 " +
    "[&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none " +
    "[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-0 " +
    "[&::-webkit-slider-thumb]:bg-primary2 " +
    "[&::-moz-range-track]:h-1.5 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:bg-black70 " +
    "[&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:cursor-pointer " +
    "[&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-primary2";

const TEMP_MIN = -10;
const TEMP_MAX = 50;
const DEFAULT_THRESHOLD = 30;

/** Fallback if GET /control/thermal_palette/modes is unavailable. */
const FALLBACK_PALETTES: ThermalPaletteInfo[] = [
    { name: "white_hot", wire_id: "01" },
    { name: "sepia", wire_id: "03" },
    { name: "ironbow", wire_id: "04" },
    { name: "rainbow", wire_id: "05" },
    { name: "night", wire_id: "06" },
    { name: "aurora", wire_id: "07" },
    { name: "red_hot", wire_id: "08" },
    { name: "jungle", wire_id: "09" },
    { name: "medical", wire_id: "0A" },
    { name: "black_hot", wire_id: "0B" },
    { name: "glory_hot", wire_id: "0C" },
];

const SELECT_CLASS =
    "rounded-r5 border border-black60 bg-black100 px-3 py-2 text-h5 text-white " +
    "outline-none focus:border-primary2 disabled:opacity-50";

interface SkydroidThermalSettingsDialogProps {
    visible: boolean;
    onClose: () => void;
}

const SkydroidThermalSettingsDialog: React.FC<SkydroidThermalSettingsDialogProps> = ({
    visible,
    onClose,
}) => {
    const { t } = useTranslation(["settings", "common"]);
    const { addToast } = useToast();
    const { selectedARC } = useARC();
    const isSecure = useIsHttpSecure();
    const arcId = useFleetArcId();
    const host = selectedARC?.host ?? "";
    const baseUrl = useMemo(
        () => getGimbalGatewayBaseUrl(selectedARC?.host ?? null, isSecure, arcId),
        [selectedARC?.host, isSecure, arcId]
    );

    const [threshold, setThreshold] = useState(DEFAULT_THRESHOLD);
    const [palette, setPalette] = useState("white_hot");
    const [palettes, setPalettes] = useState<ThermalPaletteInfo[]>(FALLBACK_PALETTES);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [applyingPalette, setApplyingPalette] = useState(false);

    useEffect(() => {
        if (!visible || !host) return;
        let cancelled = false;
        setLoading(true);
        void (async () => {
            try {
                const data = await getThermalThreshold(host, isSecure, arcId);
                if (!cancelled && Number.isFinite(data.threshold_c)) {
                    setThreshold(
                        Math.min(TEMP_MAX, Math.max(TEMP_MIN, data.threshold_c))
                    );
                }
            } catch {
                if (!cancelled) {
                    setThreshold(DEFAULT_THRESHOLD);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [visible, host, isSecure, arcId]);

    useEffect(() => {
        if (!visible || !baseUrl) {
            setPalettes(FALLBACK_PALETTES);
            return;
        }
        let cancelled = false;
        void (async () => {
            try {
                const [paletteModes, savedPalette] = await Promise.all([
                    getGimbalThermalPaletteModes(baseUrl),
                    getGimbalThermalPalette(baseUrl),
                ]);
                if (cancelled) return;
                if (paletteModes.length > 0) {
                    setPalettes(paletteModes);
                }
                if (savedPalette.palette) {
                    setPalette(savedPalette.palette);
                }
            } catch {
                if (!cancelled) {
                    setPalettes(FALLBACK_PALETTES);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [visible, baseUrl]);

    const applyPalette = useCallback(
        async (next: string) => {
            if (!baseUrl || applyingPalette) return;
            setApplyingPalette(true);
            try {
                const out = await postGimbalThermalPalette(baseUrl, next);
                const applied = out.palette || next;
                setPalette(applied);
                addToast({
                    type: "success",
                    message: t("payload.thermalPaletteSaved", {
                        palette: t(`payload.thermalPalettes.${applied}`, {
                            defaultValue: applied,
                        }),
                    }),
                });
            } catch (e) {
                addToast({
                    type: "error",
                    message:
                        e instanceof Error && e.message
                            ? e.message
                            : t("payload.thermalPaletteSaveFailed"),
                });
            } finally {
                setApplyingPalette(false);
            }
        },
        [baseUrl, applyingPalette, addToast, t]
    );

    const handleApply = useCallback(async () => {
        if (!host || saving) return;
        setSaving(true);
        try {
            const data = await putThermalThreshold(host, threshold, isSecure, arcId);
            if (Number.isFinite(data.threshold_c)) {
                setThreshold(data.threshold_c);
            }
            addToast({
                type: "success",
                message: t("payload.thermalAlertSaved", { value: threshold }),
            });
            onClose();
        } catch (e) {
            addToast({
                type: "error",
                message:
                    e instanceof Error && e.message
                        ? e.message
                        : t("payload.thermalAlertSaveFailed"),
            });
        } finally {
            setSaving(false);
        }
    }, [host, saving, threshold, isSecure, arcId, addToast, t, onClose]);

    return (
        <AnimatedModal
            visible={visible}
            onClose={onClose}
            className="relative w-full max-w-md rounded-r10 border border-black80 bg-black90 p-6"
        >
            <FunctionalButton
                icon={<CloseIcon />}
                className="absolute right-3 top-3"
                onClick={onClose}
            />
            <h2 className="mb-2 pr-8 text-h4 font-semibold text-white">
                {t("payload.thermalSettingsTitle")}
            </h2>
            <p className="mb-6 text-h5 text-black40">{t("payload.thermalSettingsDescription")}</p>

            <div className="mb-6 flex flex-col gap-2">
                <label className="text-h5 text-black40" htmlFor="c13-thermal-palette">
                    {t("payload.thermalPaletteLabel")}
                </label>
                <select
                    id="c13-thermal-palette"
                    value={palette}
                    disabled={!baseUrl || applyingPalette}
                    onChange={(e) => {
                        const next = e.target.value;
                        setPalette(next);
                        void applyPalette(next);
                    }}
                    className={SELECT_CLASS}
                    aria-label={t("payload.thermalPaletteLabel")}
                >
                    {palettes.map((m) => (
                        <option key={m.wire_id} value={m.name}>
                            {t(`payload.thermalPalettes.${m.name}`, {
                                defaultValue: m.name,
                            })}
                        </option>
                    ))}
                </select>
                <p className="text-h5 text-black60">{t("payload.thermalPaletteHint")}</p>
            </div>

            <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                    <span className="text-h5 text-black40">
                        {t("payload.thermalAlertLabel")}
                    </span>
                    <span className="font-mono text-h5 tabular-nums text-white">
                        {loading ? "…" : `${threshold.toFixed(0)} °C`}
                    </span>
                </div>
                <input
                    type="range"
                    min={TEMP_MIN}
                    max={TEMP_MAX}
                    step={1}
                    value={threshold}
                    disabled={loading || saving || !host}
                    onChange={(e) => {
                        const next = Number.parseFloat(e.target.value);
                        if (Number.isFinite(next)) setThreshold(next);
                    }}
                    className={RANGE_CLASS}
                    aria-label={t("payload.thermalAlertLabel")}
                />
                <div className="flex justify-between text-h5 text-black60">
                    <span>{TEMP_MIN} °C</span>
                    <span>{TEMP_MAX} °C</span>
                </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
                <FunctionalButton
                    title={t("common:cancel")}
                    className="rounded-r5 border border-black60 bg-black90 px-4 py-2 !normal-case text-h5 font-semibold text-white hover:border-black40"
                    onClick={onClose}
                    disabled={saving || applyingPalette}
                />
                <FunctionalButton
                    title={saving ? t("common:saving") : t("common:save")}
                    className="rounded-r5 border border-primary2 bg-primary2 px-4 py-2 !normal-case text-h5 font-semibold text-white hover:bg-primary1 disabled:opacity-50"
                    onClick={() => void handleApply()}
                    disabled={loading || saving || !host}
                />
            </div>
        </AnimatedModal>
    );
};

export default SkydroidThermalSettingsDialog;
